import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "./api";

// Shared hook: pulls live Centres + Batches from the DB. To stop refetching the
// same lists on every admin tab switch (14 tabs × 2 requests each = 28 wasted
// requests per session), we cache the two lists at module scope and share a
// single in-flight promise across concurrent mounts. Any mutation (add/edit/
// delete/rename) calls `reload()` to refresh both.
let cache = { centres: null, batches: null };
let inflight = null;
const subscribers = new Set();
const notify = () => subscribers.forEach((fn) => fn(cache));

const fetchAll = () => {
  if (inflight) return inflight;
  inflight = Promise.all([
    api.get("/admin/centres").then((r) => (cache.centres = r.data || [])).catch(() => (cache.centres = [])),
    api.get("/admin/batches").then((r) => (cache.batches = r.data || [])).catch(() => (cache.batches = [])),
  ]).finally(() => { inflight = null; notify(); });
  return inflight;
};

export const useTaxonomy = () => {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const on = () => setTick((n) => n + 1);
    subscribers.add(on);
    if (cache.centres === null || cache.batches === null) fetchAll();
    return () => { subscribers.delete(on); };
  }, []);

  const reload = useCallback(() => {
    cache = { centres: null, batches: null };
    return fetchAll();
  }, []);

  const centres = cache.centres || [];
  const batches = cache.batches || [];
  const centreNames = useMemo(() => centres.map((c) => c.name), [centres, tick]);  // eslint-disable-line react-hooks/exhaustive-deps
  const batchNames = useMemo(() => batches.map((b) => b.name), [batches, tick]);  // eslint-disable-line react-hooks/exhaustive-deps
  const centreForBatch = useCallback((name) => {
    const b = batches.find((x) => x.name === name);
    return b ? (b.centre || "") : "";
  }, [batches]);  // eslint-disable-line react-hooks/exhaustive-deps

  return { centres, batches, centreNames, batchNames, centreForBatch, reload, ready: cache.centres !== null && cache.batches !== null };
};
